import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, Upload, Download, Camera, Barcode, ScanLine,
  AlertTriangle, CheckCircle2, X, ClipboardPaste, FileSpreadsheet, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePermission } from "@/hooks/usePermission";

const CLAS_BADGE = {
  C: "bg-[hsl(38_92%_55%/0.18)] text-[hsl(38_92%_55%)] border-[hsl(38_92%_55%/0.4)]",
  D: "bg-destructive/15 text-destructive border-destructive/40",
};

function CameraScanner({ open, onClose, onDetected }) {
  const containerId = "sn-qr-reader-region";
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function boot() {
      try {
        const mod = await import("html5-qrcode");
        if (cancelled) return;
        const { Html5Qrcode } = mod;
        const scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        const cams = await Html5Qrcode.getCameras();
        if (!cams || cams.length === 0) {
          setError("No se detectaron cámaras en este dispositivo.");
          return;
        }
        // Prefer back camera on mobile
        const back = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
        await scanner.start(
          back.id,
          { fps: 12, qrbox: { width: 260, height: 160 } },
          (decoded) => {
            onDetected?.(decoded);
          },
          () => { /* ignore scan errors */ }
        );
        setReady(true);
      } catch (err) {
        setError(err?.message || "No se pudo iniciar la cámara.");
      }
    }
    boot();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {}).finally(() => {
          try { s.clear(); } catch { /* noop */ }
        });
      }
      scannerRef.current = null;
      setReady(false);
    };
  }, [open, onDetected]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/85 z-[70] flex flex-col items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3 text-white">
          <div className="flex items-center gap-2">
            <ScanLine className="h-4 w-4" />
            <span className="text-sm font-medium">Escanear SN con cámara</span>
          </div>
          <Button size="icon" variant="secondary" onClick={onClose} data-testid="sn-camera-close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="rounded-md overflow-hidden bg-black border border-border relative">
          <div id={containerId} style={{ width: "100%" }} />
          {!ready && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
              Iniciando cámara…
            </div>
          )}
        </div>
        {error && (
          <div className="mt-3 text-sm text-destructive bg-destructive/10 border border-destructive/40 rounded-md p-2 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
        <p className="text-[11px] text-white/70 mt-2 text-center">
          Apunta al código de barras o QR. El SN se agregará automáticamente al detectarlo.
        </p>
      </div>
    </div>
  );
}

export default function SnDamagedTab({ otId, folio, items: otItems = [] }) {
  const qc = useQueryClient();
  const canManage = usePermission("sn_manage");
  const canExport = usePermission("ot_export");

  const [selectedPartida, setSelectedPartida] = useState("");
  const [selectedClas, setSelectedClas] = useState("C");
  const [snInput, setSnInput] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [pasteOpen, setPasteOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const uploadInputRef = useRef(null);
  const snInputRef = useRef(null);

  // Only partidas with C or D limit make sense
  const eligiblePartidas = useMemo(() => {
    return (otItems || [])
      .map((it, idx) => ({
        idx: idx + 1,
        sku: it.sku,
        po: it.po,
        pedimento: it.pedimento,
        pz: parseInt(it.pz) || 0,
        c: parseInt(it.clasificacion_c) || 0,
        d: parseInt(it.clasificacion_d) || 0,
      }))
      .filter((p) => p.c > 0 || p.d > 0);
  }, [otItems]);

  // Auto-select first eligible partida when list changes
  useEffect(() => {
    if (!selectedPartida && eligiblePartidas.length > 0) {
      setSelectedPartida(String(eligiblePartidas[0].idx));
      // Also default to a clas that has a limit
      const first = eligiblePartidas[0];
      if (first.c === 0 && first.d > 0) setSelectedClas("D");
    }
  }, [eligiblePartidas, selectedPartida]);

  const currentPartida = eligiblePartidas.find((p) => String(p.idx) === String(selectedPartida));

  const { data: summary, refetch: refetchSummary, isLoading: loadingSummary } = useQuery({
    queryKey: ["sn-summary", otId],
    queryFn: async () => (await api.get(`/ots/${otId}/sn/summary`)).data,
  });

  const { data: snList = [], refetch: refetchList } = useQuery({
    queryKey: ["sn-list", otId],
    queryFn: async () => (await api.get(`/ots/${otId}/sn`)).data,
  });

  const refreshAll = () => {
    refetchSummary();
    refetchList();
    qc.invalidateQueries({ queryKey: ["history", otId] });
  };

  const capacityForCurrent = useMemo(() => {
    if (!currentPartida) return { limit: 0, used: 0 };
    const limit = selectedClas === "C" ? currentPartida.c : currentPartida.d;
    const row = (summary?.items || []).find((r) => r.partida_idx === currentPartida.idx);
    const used = row ? (selectedClas === "C" ? row.sn_c_count : row.sn_d_count) : 0;
    return { limit, used };
  }, [currentPartida, selectedClas, summary]);

  const partidaClasHasLimit = capacityForCurrent.limit > 0;

  const submitSingle = async (sn) => {
    if (!currentPartida) return toast.error("Selecciona una partida");
    if (!sn || !sn.trim()) return toast.error("SN vacío");
    if (capacityForCurrent.used >= capacityForCurrent.limit) {
      toast.error(
        `Se alcanzó el límite de SN Clas. ${selectedClas} para la partida ${currentPartida.idx} (${capacityForCurrent.limit}).`
      );
      return;
    }
    setSaving(true);
    try {
      await api.post(`/ots/${otId}/sn`, {
        partida_idx: currentPartida.idx,
        clasificacion: selectedClas,
        sn: sn.trim(),
        captured_via: "manual",
      });
      toast.success(`SN ${sn.trim().toUpperCase()} agregada`);
      setSnInput("");
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al agregar SN");
    } finally {
      setSaving(false);
      snInputRef.current?.focus();
    }
  };

  const handleSubmitManual = async (e) => {
    e?.preventDefault?.();
    await submitSingle(snInput);
  };

  const submitBulk = async (rawList, capturedVia = "bulk") => {
    if (!currentPartida) return toast.error("Selecciona una partida");
    const cleaned = rawList
      .map((s) => (s || "").trim())
      .filter(Boolean);
    if (cleaned.length === 0) return toast.error("No hay SN para procesar");
    setSaving(true);
    try {
      const res = await api.post(`/ots/${otId}/sn/bulk`, {
        partida_idx: currentPartida.idx,
        clasificacion: selectedClas,
        captured_via: capturedVia,
        sns: cleaned,
      });
      const inserted = res.data.inserted_count || 0;
      const skipped = (res.data.skipped || []).length;
      toast.success(`${inserted} SN agregadas${skipped ? `, ${skipped} omitidas` : ""}`);
      if (skipped) {
        const reasons = (res.data.skipped || []).slice(0, 3).map((s) => `${s.sn}: ${s.reason}`).join(" · ");
        toast.warning(`Omitidas: ${reasons}${(res.data.skipped || []).length > 3 ? "…" : ""}`);
      }
      setPasteText("");
      setPasteOpen(false);
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar SN en lote");
    } finally {
      setSaving(false);
    }
  };

  const handlePasteSubmit = () => {
    const list = pasteText.split(/\r?\n|;|,/).map((s) => s.trim()).filter(Boolean);
    submitBulk(list, "paste");
  };

  const deleteSn = async (id) => {
    try {
      await api.delete(`/ots/${otId}/sn/${id}`);
      toast.success("SN eliminada");
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/ots/${otId}/sn/template-excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SN_TEMPLATE_${folio}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descargar la plantilla");
    }
  };

  const downloadExport = async () => {
    try {
      const res = await api.get(`/ots/${otId}/sn/export-excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SN_Danadas_${folio}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descargar el export");
    }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setSaving(true);
    try {
      const res = await api.post(`/ots/${otId}/sn/upload-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      toast.success(`${res.data.inserted_count} SN cargadas desde Excel`);
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir Excel de SN");
    } finally {
      setSaving(false);
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  };

  const onCameraDetected = async (code) => {
    // Fire-and-forget single insert; keep camera open for continuous scanning.
    const sn = (code || "").trim();
    if (!sn) return;
    try {
      await api.post(`/ots/${otId}/sn`, {
        partida_idx: currentPartida?.idx,
        clasificacion: selectedClas,
        sn,
        captured_via: "camera",
      });
      toast.success(`SN ${sn.toUpperCase()} escaneado y agregado`);
      refreshAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al agregar SN";
      // Show only the error toast; avoid noisy repeats — cooldown handled by scanner FPS.
      toast.error(detail);
    }
  };

  const totals = summary?.totals || { total_captured: 0, total_expected: 0, sn_c_captured: 0, sn_d_captured: 0, clas_c_limit: 0, clas_d_limit: 0 };
  const progress = totals.total_expected > 0 ? Math.round((totals.total_captured / totals.total_expected) * 100) : 0;

  // Group snList by partida then clasificacion — MUST be before any early return (hook order rule)
  const grouped = useMemo(() => {
    const map = {};
    for (const s of snList) {
      const k = s.partida_idx;
      if (!map[k]) map[k] = { partida_idx: k, sku: s.sku, po: s.po, pedimento: s.pedimento, C: [], D: [] };
      map[k][s.clasificacion]?.push(s);
    }
    return Object.values(map).sort((a, b) => a.partida_idx - b.partida_idx);
  }, [snList]);

  if (eligiblePartidas.length === 0) {
    return (
      <Card className="bg-card border-dashed border-border">
        <CardContent className="p-8 text-center">
          <Barcode className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-sm font-medium">No hay partidas con piezas dañadas</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Esta OT no tiene piezas en Clasificación C o D. Los SN solo se capturan para piezas dañadas.
            Edita la OT y agrega piezas en Clas. C o D para poder registrar SN.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Overall progress + actions */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Progreso global de SN</div>
              <div className="text-sm mt-0.5">
                <span data-testid="sn-total-captured" className="font-mono font-semibold text-foreground">{totals.total_captured}</span>
                <span className="text-muted-foreground"> / {totals.total_expected} piezas dañadas</span>
                <Badge className={`ml-2 border ${CLAS_BADGE.C}`}>C: {totals.sn_c_captured}/{totals.clas_c_limit}</Badge>
                <Badge className={`ml-1 border ${CLAS_BADGE.D}`}>D: {totals.sn_d_captured}/{totals.clas_d_limit}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button size="sm" variant="ghost" onClick={refreshAll} data-testid="sn-refresh" title="Actualizar">
                <RefreshCw className={`h-3.5 w-3.5 ${loadingSummary ? "animate-spin" : ""}`} />
              </Button>
              <Button size="sm" variant="secondary" onClick={downloadTemplate} data-testid="sn-download-template">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Plantilla
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => uploadInputRef.current?.click()}
                disabled={!canManage || saving}
                data-testid="sn-upload-excel"
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Subir Excel
              </Button>
              <input
                ref={uploadInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={(e) => uploadExcel(e.target.files?.[0])}
                data-testid="sn-upload-input"
              />
              <Button size="sm" onClick={downloadExport} disabled={!canExport} data-testid="sn-export-excel">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Descargar SN
              </Button>
            </div>
          </div>
          <Progress value={progress} className="h-2" data-testid="sn-global-progress" />
        </CardContent>
      </Card>

      {/* Upload result */}
      {uploadResult && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-3 text-xs space-y-1">
            <div className="flex items-center justify-between">
              <div className="font-medium">
                Carga por Excel: <span className="font-mono text-primary">{uploadResult.inserted_count}</span> agregadas
                {uploadResult.skipped?.length > 0 && <> · <span className="text-[hsl(var(--status-pending))]">{uploadResult.skipped.length} omitidas</span></>}
              </div>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setUploadResult(null)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
            {uploadResult.skipped?.length > 0 && (
              <ul className="text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
                {uploadResult.skipped.slice(0, 20).map((s, i) => (
                  <li key={i} className="font-mono text-[11px]">• {s.sn}: <span className="text-destructive/80">{s.reason}</span></li>
                ))}
                {uploadResult.skipped.length > 20 && <li className="italic">…y {uploadResult.skipped.length - 20} más</li>}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Capture card */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Barcode className="h-4 w-4 text-primary" /> Capturar SN
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Partida</Label>
              <Select value={selectedPartida} onValueChange={setSelectedPartida}>
                <SelectTrigger className="mt-1" data-testid="sn-partida-select">
                  <SelectValue placeholder="Selecciona partida" />
                </SelectTrigger>
                <SelectContent>
                  {eligiblePartidas.map((p) => {
                    const row = (summary?.items || []).find((r) => r.partida_idx === p.idx);
                    const c = row?.sn_c_count ?? 0;
                    const d = row?.sn_d_count ?? 0;
                    return (
                      <SelectItem key={p.idx} value={String(p.idx)} className="font-mono">
                        #{p.idx} · {p.sku || "-"} · C {c}/{p.c} · D {d}/{p.d}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Clasificación (solo daño)</Label>
              <Select value={selectedClas} onValueChange={setSelectedClas}>
                <SelectTrigger className="mt-1" data-testid="sn-clas-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="C" disabled={!currentPartida || currentPartida.c === 0}>Clas. C (Cosmético)</SelectItem>
                  <SelectItem value="D" disabled={!currentPartida || currentPartida.d === 0}>Clas. D (Dañado)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {currentPartida && (
            <div className="text-[11px] text-muted-foreground border border-border rounded-md bg-muted/30 p-2">
              Partida seleccionada: <span className="font-mono text-foreground">#{currentPartida.idx} · SKU {currentPartida.sku || "-"} · Pedimento {currentPartida.pedimento || "-"}</span>
              <span className="ml-2">
                Capacidad Clas. {selectedClas}: <span className="font-mono text-foreground font-semibold">{capacityForCurrent.used}/{capacityForCurrent.limit}</span>
              </span>
              {!partidaClasHasLimit && (
                <span className="ml-2 text-destructive">Esta partida no tiene piezas Clas. {selectedClas}.</span>
              )}
            </div>
          )}

          {/* Manual/USB input */}
          <form onSubmit={handleSubmitManual} className="flex flex-col sm:flex-row gap-2">
            <Input
              ref={snInputRef}
              value={snInput}
              onChange={(e) => setSnInput(e.target.value)}
              placeholder="Escanea o teclea el SN y presiona Enter"
              className="font-mono h-10"
              autoFocus
              disabled={!canManage || !partidaClasHasLimit}
              data-testid="sn-manual-input"
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={!canManage || !snInput.trim() || saving || !partidaClasHasLimit}
                data-testid="sn-manual-submit"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Agregar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPasteOpen(true)}
                disabled={!canManage || !partidaClasHasLimit}
                data-testid="sn-open-paste"
                title="Pegar múltiples SN"
              >
                <ClipboardPaste className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Pegar múltiples</span>
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCameraOpen(true)}
                disabled={!canManage || !partidaClasHasLimit}
                data-testid="sn-open-camera"
                title="Escanear con cámara"
              >
                <Camera className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Cámara</span>
              </Button>
            </div>
          </form>
          <p className="text-[11px] text-muted-foreground">
            Tip: si usas una <b className="text-foreground">pistola escáner USB</b>, simplemente enfoca este campo y dispara — se comportará como Enter.
          </p>
        </CardContent>
      </Card>

      {/* Per-partida capture summary + list */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">SN Capturadas ({snList.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {grouped.length === 0 && (
            <div className="text-center py-6 text-sm text-muted-foreground">Sin SN capturadas todavía.</div>
          )}
          {grouped.map((g) => (
            <div key={g.partida_idx} className="border border-border rounded-md p-3" data-testid={`sn-group-${g.partida_idx}`}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Partida #{g.partida_idx} · <span className="font-mono text-foreground/80">{g.sku || "-"}</span>
                {g.pedimento && <> · Pedimento <span className="font-mono text-foreground/80">{g.pedimento}</span></>}
              </div>
              {["C", "D"].map((cl) => {
                const list = g[cl];
                if (!list?.length) return null;
                return (
                  <div key={cl} className="mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                      <Badge className={`border ${CLAS_BADGE[cl]}`}>Clas. {cl}</Badge>
                      <span>{list.length}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {list.map((s) => (
                        <div
                          key={s.id}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs group"
                          title={`Por ${s.created_by_name || s.created_by} · ${(s.created_at || "").substring(0, 16).replace("T", " ")} · ${s.captured_via}`}
                        >
                          <span className="font-mono text-foreground">{s.sn}</span>
                          {canManage && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-destructive opacity-70 group-hover:opacity-100"
                                  title="Eliminar SN"
                                  data-testid={`sn-delete-${s.id}`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Eliminar SN {s.sn}?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Se removerá de la partida {s.partida_idx}, Clas. {s.clasificacion}.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteSn(s.id)}>Eliminar</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Paste multiple dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pegar múltiples SN</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="text-muted-foreground">
              Se cargarán a la partida <span className="font-mono text-foreground">#{currentPartida?.idx}</span> ·
              Clas. <span className="font-mono text-foreground">{selectedClas}</span>. Una SN por línea, coma o punto y coma.
            </div>
            <Textarea
              rows={10}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={"SN0001\nSN0002\nSN0003"}
              className="font-mono text-xs"
              data-testid="sn-paste-textarea"
            />
            <div className="text-muted-foreground flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-400" />
              {pasteText.split(/\r?\n|;|,/).map((s) => s.trim()).filter(Boolean).length} SN detectadas.
              Capacidad libre: <span className="font-mono text-foreground">{Math.max(0, capacityForCurrent.limit - capacityForCurrent.used)}</span>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPasteOpen(false)}>Cancelar</Button>
            <Button onClick={handlePasteSubmit} disabled={saving || !pasteText.trim()} data-testid="sn-paste-submit">
              Cargar en lote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera scanner */}
      <CameraScanner open={cameraOpen} onClose={() => setCameraOpen(false)} onDetected={onCameraDetected} />
    </div>
  );
}
